import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { Face } from '@/features/game/Dice'
import { questsKey } from '@/features/quests/QuestCard'
import type { Skins } from '@/shared/game/types'
import { buyItem, equipItem, fetchShop, type ShopItem, type Showcase } from './api'
import { CoinIcon } from './CoinIcon'
import { diceStyle, emblemPath, frameOf } from './skins'

export const shopKey = ['shop'] as const

type Props = {
  signedIn: boolean
  onSignIn: () => void
}

/** Виды вещей по порядку показа — и как они называются. */
const KINDS: { kind: keyof Skins; title: string; none: string }[] = [
  { kind: 'dice', title: 'Кубики', none: 'Обычные' },
  { kind: 'token', title: 'Значок на фишках', none: 'Без значка' },
  { kind: 'frame', title: 'Рамка аватара', none: 'Без рамки' },
]

/**
 * Магазин косметики (docs/QUESTS.md, «На что тратить»).
 *
 * Окном поверх экрана, как профиль: заглядывают сюда на минуту, в том числе
 * посреди партии, — и уводить за этим с доски значит уводить с хода.
 *
 * Здесь продаётся только то, что видят соперники: кубики видит весь стол
 * при броске, значок — на каждой фишке. Цвет фишки не продаётся: это игрок.
 */
export function ShopScreen({ signedIn, onSignIn }: Props) {
  return (
    <div className="shop">
      <h2 className="hero__title">Магазин</h2>

      {signedIn ? (
        <Shelves />
      ) : (
        <div className="card">
          <p>Монеты зарабатываются заданиями дня, а заданиям нужна учётная запись.</p>
          <button type="button" className="primary shop__signin" onClick={onSignIn}>
            войти
          </button>
        </div>
      )}
    </div>
  )
}

function Shelves() {
  const client = useQueryClient()
  const shop = useQuery({ queryKey: shopKey, queryFn: fetchShop, staleTime: 0 })

  const saved = (showcase: Showcase) => {
    client.setQueryData(shopKey, showcase)
    // Баланс виден и в карточке заданий — пусть и там будет свежим.
    void client.invalidateQueries({ queryKey: questsKey })
  }

  const buy = useMutation({ mutationFn: buyItem, onSuccess: saved })
  const equip = useMutation({
    mutationFn: ({ kind, code }: { kind: keyof Skins; code: string | null }) =>
      equipItem(kind, code),
    onSuccess: saved,
  })

  if (shop.isError) return <p className="banner banner--error">магазин не открылся</p>
  if (shop.data === undefined) return <p className="muted shop__wait">открываем…</p>

  const { coins, items, equipped } = shop.data
  const busy = buy.isPending || equip.isPending
  const failure = buy.error ?? equip.error

  return (
    <>
      <div className="card">
        <p className="shop__coins">
          У вас <strong>{coins}</strong> монет. Их дают задания дня и приглашения.
        </p>
        {failure !== null && <p className="banner banner--error">{reason(failure)}</p>}
      </div>

      {KINDS.map(({ kind, title, none }) => (
        <section key={kind} className="card">
          <h3 className="card__title">{title}</h3>
          <ul className="shop__items">
            <li className="shop__item">
              <Preview kind={kind} code={null} />
              <span className="shop__title">{none}</span>
              {equipped[kind] === null ? (
                <span className="shop__worn">выбрано</span>
              ) : (
                <button
                  type="button"
                  className="ghost"
                  disabled={busy}
                  onClick={() => equip.mutate({ kind, code: null })}
                >
                  выбрать
                </button>
              )}
            </li>
            {items
              .filter((item) => item.kind === kind)
              .map((item) => (
                <li key={item.code} className="shop__item">
                  <Preview kind={kind} code={item.code} />
                  <span className="shop__title">{item.title}</span>
                  <Action
                    item={item}
                    coins={coins}
                    busy={busy}
                    onBuy={() => buy.mutate(item.code)}
                    onWear={() => equip.mutate({ kind, code: item.code })}
                  />
                </li>
              ))}
          </ul>
        </section>
      ))}
    </>
  )
}

function Action({
  item,
  coins,
  busy,
  onBuy,
  onWear,
}: {
  item: ShopItem
  coins: number
  busy: boolean
  onBuy: () => void
  onWear: () => void
}) {
  if (item.equipped) return <span className="shop__worn">выбрано</span>

  if (item.owned) {
    return (
      <button type="button" className="ghost" disabled={busy} onClick={onWear}>
        выбрать
      </button>
    )
  }

  // Не хватает — кнопка видна, но выключена: цена — это цель, к которой
  // ведут задания, и прятать её незачем.
  // Цена — числом и монетой, а не «150 монет»: слово занимало на телефоне
  // половину строки, и названию вещи не хватало места.
  return (
    <button
      type="button"
      className="primary shop__price"
      disabled={busy || coins < item.price}
      aria-label={`купить за ${item.price} монет`}
      onClick={onBuy}
    >
      {item.price}
      <CoinIcon />
    </button>
  )
}

/** Образец вещи: грань кубика или фишка со значком. */
function Preview({ kind, code }: { kind: keyof Skins; code: string | null }) {
  if (kind === 'dice') {
    return (
      <span className="die shop__die" style={diceStyle(code)} aria-hidden="true">
        <Face value={5} />
      </span>
    )
  }

  if (kind === 'frame') {
    const frame = frameOf(code)

    return (
      <svg className="shop__token" viewBox="-0.5 -0.5 1 1" aria-hidden="true">
        <circle r={0.3} fill="var(--accent)" />
        {frame !== null && (
          <>
            <circle r={0.37} fill="none" stroke={frame.ring} strokeWidth={0.12} />
            <circle r={0.3} fill="none" stroke={frame.edge} strokeWidth={0.04} />
          </>
        )}
      </svg>
    )
  }

  const emblem = emblemPath(code)

  return (
    <svg className="shop__token" viewBox="-0.5 -0.5 1 1" aria-hidden="true">
      <circle r={0.44} fill="var(--accent)" />
      {emblem !== null && <path d={emblem} fill="#fff" opacity={0.9} />}
    </svg>
  )
}

/** Текст отказа сервера: «не хватает монет», «уже куплено». */
function reason(error: unknown): string {
  if (isAxiosError(error)) {
    const message = (error.response?.data as { message?: unknown } | undefined)?.message

    if (typeof message === 'string' && message !== '') return message
  }

  return 'не получилось'
}
